/**
 * `/plans` — the trainer's programmes, as KANON `Programy v2 - D3 (0.8)` draws them: the roster's
 * header over a master-detail pane.
 *
 * ── Composition, and what each piece owns ────────────────────────────────────────────────────
 * `ScreenHeader` is the clients roster's band, shared: the `h1`, the ember square, and three
 * figures that are an em dash until the read lands. `ScopeSwitch` chooses which list. `ListToolbar`
 * narrows it. `ListRows` draws it and owns the keyboard path. `ListStates` stands in the row box in
 * the three states that have no rows. `Inspector` describes whichever plan the pointer is on, and
 * owns its own two reads.
 *
 * ── The three figures are over the WHOLE library, never the filtered view ────────────────────
 * The canon's own no-results specimen still reads `8 · 6 · 2` with a phrase active. They answer
 * "what do you have", not "what are you looking at".
 *
 * ── Nothing on this screen moves when data lands ─────────────────────────────────────────────
 * Every box is stated before the read answers: the header renders in all three states, the pane and
 * both cards are laid out by the stylesheet, and the wait is eight rows at the row's own height.
 * The gate hides the BARS, not the box. Verify it with latency injected — the local backend answers
 * in about 5ms and hides everything above.
 *
 * The gate itself is NOT held here any more (21 September 2026). It lives in `usePlanLibrary`, so
 * `library.status` already carries the bars' minimum life and every fork on this screen — the list
 * card's five ways, the toolbar's disabled state, the pane's absence — turns at the same moment the
 * bars retire. A screen that gated here and forked on the read's own `pending` showed the trainer
 * two entrances for one load; `useSkeletonGate`'s header has the table.
 *
 * ── The screen ENTERS, once, as one object ───────────────────────────────────────────────────
 * Owner decision **D27**, 4 September 2026: `/plans` gets the clients roster's entrance. It is the
 * roster's own step — `screenMotion('rise')`, the handoff's 300ms on `SCREEN_EASE` after a 120ms
 * base delay — with one difference that is the owner's word rather than a reading of the roster:
 * the roster cascades its two panes at `index` 0 and 1, and this screen animates the WHOLE measure
 * as one element. A master-detail pane is one object; sliding the inspector in behind the list
 * would be two.
 *
 * It runs on MOUNT and never again: nothing here is keyed, so a filter, a search or a scope switch
 * re-renders under a settled transform. The INSPECTOR's own swap is untouched and is a different
 * animation on a different clock — it is keyed on the selection tick inside `Inspector`.
 *
 * `prefers-reduced-motion` is a PARAMETER, never the stylesheet's problem: `base.css`'s 0.001ms cap
 * governs CSS animations and Motion drives the Web Animations API, so a surface that relied on that
 * block would ignore the preference while looking compliant. `useScreenMotionPreference` reads it
 * and `screenMotion` returns a step whose start state is its end state.
 *
 * The entrance moves the whole measure by the step's own 14px of `translateY`, which is a TRANSFORM and not a
 * layout: nothing reflows, and the no-jump sampler in `e2e/plan-library.spec.ts` measures boxes
 * against the wrapper that carries it, exactly as it already does for the inspector's swap.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';

import { CreatePlanDialog } from '~/features/create-plan';
import { screenMotion, useScreenMotionPreference } from '~/shared/design-system/motion';
import { useDocumentTitle } from '~/shared/lib/useDocumentTitle';
import ember from '~/shared/ui/emberButton.module.css';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import { ScreenHeader, ScreenHeaderPlus } from '~/shared/ui/ScreenHeader';
import header from '~/shared/ui/screenHeader.module.css';
import shell from '~/shared/ui/canonScreen.module.css';

import { useInspectorPrefetch, type InspectorPrefetchRow } from '../lib/useInspectorPrefetch';
import { useTypeToSearch } from '../lib/useTypeToSearch';
import { usePlanLibrary, type PlanLibraryList } from '../lib/usePlanLibrary';
import { usePointedRow } from '~/shared/lib/usePointedRow';
import { clientPlanSubject, templateSubject } from '../model/inspectorSubject';
import { NO_FILTER, activeFacetCount } from '../model/planLibraryFilter';
import { inspectorAbsenceOf } from '../model/planListStatus';
import { planScopeContext } from '../model/planScope';
import { Inspector } from './Inspector';
import { ListRows, clientRowKey, templateRowKey } from './ListRows';
import { ClientPlansEmpty, ListEmpty, ListError, ListNoResults, ListSkeleton } from './ListStates';
import { ListToolbar } from './ListToolbar';
import { ScopeSwitch } from './ScopeSwitch';
import styles from './PlanLibraryScreen.module.css';

/** The three header figures, in the canon's order, with the plural key each one reads. */
const STAT_KEYS = ['total', 'assigned', 'idle'] as const;

/**
 * What the prefetch queue needs from a list: which PLAN each row is about, and its photograph.
 *
 * A client's plan row is about the client's own FORK rather than the template it came from, which
 * is the one thing the two scopes spell differently — `model/inspectorSubject` resolves the same
 * fact for the pane. Written once and called twice: with the scope's whole list, which is what the
 * queue seeds from, and with the narrowed list, which is what `promote` indexes into.
 */
function warmRowsOf(list: PlanLibraryList): InspectorPrefetchRow[] {
  return list.scope === 'templates'
    ? list.rows.map((row) => ({ id: row.id, heroUrl: row.heroUrl }))
    : list.rows.map((row) => ({ id: row.planTemplateId, heroUrl: row.heroUrl }));
}

export function PlanLibraryScreen() {
  const { t } = useTranslation();
  const library = usePlanLibrary();
  const reducedMotion = useScreenMotionPreference();
  const [creating, setCreating] = useState(false);
  // One ref, two consumers: the header attaches it to the ember square and the dialog hands focus
  // back to it on close. Base UI cannot find the button on its own — it is not a `Dialog.Trigger`,
  // because the header owns the layout — and without it focus lands on `<body>`.
  const createButton = useRef<HTMLButtonElement>(null);
  const title = useRef<HTMLHeadingElement>(null);
  // The search input itself, so a letter typed over the list lands in it.
  const searchField = useRef<HTMLInputElement>(null);
  // The list card, which the filter panel hangs off: KANON puts the panel over its top-left corner,
  // so the box it is positioned against is this screen's to name.
  const listCard = useRef<HTMLElement>(null);

  const openCreate = () => {
    setCreating(true);
  };
  // A bare printable key goes into the search box rather than into a wizard. See `useTypeToSearch`
  // for what replaced the unadvertised `N` shortcut and why.
  useTypeToSearch(searchField, !creating);

  // The same string as the `h1`, deliberately: the tab and the heading name one screen.
  useDocumentTitle(t('web:planLibrary.title'));

  const isReady = library.status === 'ready';
  const scopeContext = planScopeContext(library.scope);
  // What the live region says. The failure's own sentence while the read is down, and otherwise how
  // many rows the list is showing — which is the fact a filter, a search or a scope switch changes
  // and which nothing else on the screen speaks.
  const announcement =
    library.status === 'error'
      ? t('web:planLibrary.states.errorTitle', { context: scopeContext })
      : library.status === 'ready'
        ? t('web:planLibrary.states.rowCount', {
            count: library.list.rows.length,
            context: scopeContext,
          })
        : '';

  // ── One key per row, one plan behind it, and both scopes reduced to those two lists ─────────
  // The key is namespaced by scope (see `ListRows`) so a template's id and an assignment's id
  // cannot collide across a switch. The prefetch queue takes the PLAN behind each row, which for a
  // client's plan is their own fork rather than the template it came from.
  // Read into a local first: `library.list` is a discriminated union, and mapping its `rows` before
  // narrowing the discriminant hands the callback the UNION of both row shapes.
  const list = library.list;
  const keys =
    list.scope === 'templates'
      ? list.rows.map((row) => templateRowKey(row.id))
      : list.rows.map((row) => clientRowKey(row.assignmentId));
  const visible = warmRowsOf(list);

  const pointer = usePointedRow(keys);
  // The queue warms the first screenful of plans and their covers in view order; `promote` moves
  // whichever row the trainer touches to the head of what has not started and buys it its own read.
  //
  // It seeds from the scope's WHOLE list rather than from the narrowed one: a queue keyed on the
  // filtered rows was torn down and re-granted its budget on every character typed. See the hook.
  const prefetch = useInspectorPrefetch(warmRowsOf(library.scopeRows));
  const selectedIndex = keys.indexOf(pointer.selectedKey ?? '');
  const subject =
    selectedIndex < 0
      ? null
      : list.scope === 'templates'
        ? templateSubject(list.rows[selectedIndex]!)
        : clientPlanSubject(list.rows[selectedIndex]!);
  // Pointing at a row and warming it are ONE gesture, composed here rather than inside either hook:
  // `usePointedRow` has no business knowing about a cache, and the queue has no business knowing
  // which row is selected.
  const point = (key: string) => {
    pointer.point(key);
    const at = keys.indexOf(key);
    if (at < 0) return;
    prefetch.promote(visible[at]!);
  };

  return (
    <main className={shell.page}>
      {/* v2's measure: the content holds ~1600px and centres, and the surplus on a wide monitor
        * goes to the margins rather than into stretched panes.
        *
        * It is also the screen's ENTRANCE — see the header. One element rather than the roster's
        * two-item cascade, because a master-detail pane is one object. */}
      <motion.div className={shell.measure} {...screenMotion('rise', { reducedMotion })}>
        <ScreenHeader
          title={t('web:planLibrary.title')}
          titleRef={title}
          // Never a zero while the read is open or after it failed: "0 PROGRAMÓW · 0 PRZYPISANYCH ·
          // 0 NIEUŻYWANYCH" beside a failure is three false statements.
          absence={{
            mark: t('web:planLibrary.states.figureUnknown'),
            spoken: t('web:planLibrary.states.figureUnknownLabel'),
          }}
          // The three words do NOT decline with their figures. They are captions under a KPI — the
          // canon writes PROGRAMÓW · PRZYPISANYCH · NIEUŻYWANYCH whatever the count is, and the
          // clients roster's own band has read `aktywnych · łącznie` since it shipped. An earlier
          // revision pluralised them and printed NIEUŻYWANE beside a 2, which is the canon's word
          // for no count at all.
          stats={STAT_KEYS.map((key) => ({
            label: t(`web:planLibrary.stats.${key}`),
            value: library.summary?.[key] ?? null,
          }))}
          actions={
            <button
              type="button"
              className={`${String(ember.ember)} ${String(header.primary)}`}
              onClick={openCreate}
              ref={createButton}
              title={t('web:planCreate.open')}
              aria-label={t('web:planCreate.open')}
            >
              <ScreenHeaderPlus className={header.primaryIcon} />
            </button>
          }
        />

        <div className={styles.pane}>
          <section className={`${styles.card} ${styles.list}`} ref={listCard}>
            {/* Both figures whatever the scope is: the switch is the one control that describes the
              * list the trainer is NOT looking at. Neither is a second request — see
              * `usePlanLibrary`. */}
            <ScopeSwitch
              scope={library.scope}
              onScopeChange={library.setScope}
              templateCount={library.templateCount}
              clientPlanCount={library.clientPlanCount}
            />
            <ListToolbar
              scope={library.scope}
              filter={library.filter}
              onFilterChange={library.setFilter}
              clientOptions={library.clientOptions}
              matchCount={library.list.rows.length}
              disabled={!isReady}
              anchor={listCard}
              searchRef={searchField}
            />
            {/* One decision, in `model/planListStatus`, and the inspector below reads the same
              * value. The screen used to branch five ways here and branch the same three inputs
              * again in a different order for the pane, which agreed only because somebody kept
              * them in step. */}
            {/* Every one of the five answers takes the hand-over class, not just the rows: a
              * library that lands empty, filtered to nothing or failed replaces the same bars the
              * rows would have, and cutting it in beside a fading skeleton was the one uneven
              * arrival left on this screen. */}
            {library.listStatus === 'pending' ? (
              <ListSkeleton skeleton={library.gate.skeleton} />
            ) : library.listStatus === 'error' ? (
              <ListError
                scope={library.scope}
                onRetry={library.retry}
                className={library.gate.reveal}
              />
            ) : library.listStatus === 'noClientPlans' ? (
              <ClientPlansEmpty className={library.gate.reveal} />
            ) : library.listStatus === 'noPlans' ? (
              <ListEmpty onCreate={openCreate} className={library.gate.reveal} />
            ) : library.listStatus === 'filtered' ? (
              <ListNoResults
                term={library.filter.search.trim()}
                facets={activeFacetCount(library.filter, library.scope) > 0}
                scope={library.scope}
                onClear={() => {
                  library.setFilter(NO_FILTER);
                }}
                className={library.gate.reveal}
              />
            ) : (
              <ListRows
                list={library.list}
                pointer={{ ...pointer, point }}
                className={library.gate.reveal}
              />
            )}
            {/* The screen's ONE live region, always mounted and empty when there is nothing to
              * say — a `role="status"` inserted with its text is a region that did not exist when
              * the change happened. Without it a filter that emptied the list and a refetch that
              * failed were both silent: the rows simply were not there any more. The count is what
              * changed, and the failure's own title is what a reader needs instead of it. */}
            <LiveRegionAnnouncer message={announcement} />
          </section>

          {/* The pane stands from the FIRST frame at the canon's geometry, in every state: its own
            * empty state reserves the hero and both sections, so a library landing changes ink and
            * never the list's width beside it. */}
          <Inspector
            className={`${String(styles.card)} ${String(styles.inspector)}`}
            subject={subject}
            // Five causes, not two, and the SAME value the list card branched on above. A pane
            // that read "Dodaj pierwszy program" while the library was still on its way, or beside
            // a failure card, was stating something untrue about the trainer's own account.
            //
            // `rows` never reaches it: `subject` is non-null exactly when the list has rows.
            absence={inspectorAbsenceOf(library.listStatus)}
            // The LIST's wait, handed down: the pane's swap must not replay when the list lands
            // and hands it its first plan, and its shape must not draw bars inside the grace
            // window. Both are facts about the read beside it, not about the pane's own. See
            // `Inspector`.
            listGate={library.gate}
          />
        </div>
      </motion.div>

      {/* Mounted behind its own flag rather than kept `open={false}`: closing it must discard a
        * half-filled form rather than keep it for the next time the trainer opens it. */}
      <CreatePlanDialog
        open={creating}
        finalFocus={createButton}
        onClose={() => {
          setCreating(false);
        }}
      />
    </main>
  );
}
